import { Controller } from '@hotwired/stimulus'
import { disableForm, enableForm } from '../src/helpers'
import { loadStripe } from '@stripe/stripe-js'

export default class extends Controller {
  static targets = ['card', 'name', 'error', 'form']
  static values = {
    setupIntent: String, // Setup Intents are used for adding new cards to be charged in the future
    paymentIntent: String, // Payment intents are for processing payments that require action
    useDefaultCard: Boolean
  }

  initialize () {
    this.element.stripeController = this
  }

  async connect () {
    const stripeMeta = document.querySelector('meta[name="stripe-key"]')
    if (stripeMeta === null) {
      return
    }

    const stripeKey = stripeMeta.getAttribute('content')
    this.stripe = await loadStripe(stripeKey)
    const elements = this.stripe.elements()

    // Setup regular payments
    this.card = elements.create('card', {
      style: {
        base: {
          fontFamily: '"FolioBT-Book" , Arial, Helvetica, sans-serif',
          fontSize: `${window.innerWidth <= 992 ? 21.5 : 33}px`,
          fontSmoothing: 'antialiased'
        }
      }
    })
    this.card.mount(this.cardTarget)

    this.boundChanged = this.changed.bind(this)
    this.card.addEventListener('change', this.boundChanged)
    this.boundupdateCardFontSize = this.updateCardFontSize.bind(this)
    window.addEventListener('resize', this.boundupdateCardFontSize)
  }

  disconnect () {
    this.card.removeEventListener('change', this.boundChanged)
    window.removeEventListener('resize', this.boundupdateCardFontSize)
  }

  updateCardFontSize () {
    const fontSize = window.innerWidth <= 992 ? '21.5' : '33'
    this.card.update({ style: { base: { fontSize: `${fontSize}px` } } })
  }

  changed (event) {
    if (event.error) {
      this.errorTarget.textContent = event.error.message
    } else {
      this.errorTarget.textContent = ''
    }
  }

  enableDefaultCard () {
    this.useDefaultCardValue = true
  }

  disableDefaultCard () {
    this.useDefaultCardValue = false
  }

  async submit (event) {
    if (event.detail.formSubmission) {
      event.detail.formSubmission.stop()
    } else {
      event.preventDefault()
    }

    setTimeout(() => disableForm(this.formTarget), 50)

    // Submit form and use default payment method stored in db
    if (this.useDefaultCardValue) {
      this.completeAndsubmit()
    } else {
      // Validate element
      if (!this.cardTarget.classList.contains('StripeElement--complete')) {
        this.showError('Your payment information is incomplete.')
        return
      }

      // Validate name input
      if (this.nameTarget.value == '') {
        this.showError('Name on card is required.')
        return
      }

      // Submit form with new payment method
      // One time payments
      if (this.hasPaymentIntentValue) {
        this.handleCardPayment()

        // Updating card with setup intent
      } else if (this.hasSetupIntentValue) {
        this.setupNewCard()

        // Subscriptions simply tokenize the payment method and redirect to payment page if SCA required
      } else {
        const result = await this.stripe.createPaymentMethod({
          type: 'card',
          card: this.card,
          billing_details: {
            name: this.nameTarget.value
          }
        })

        this.handlePaymentMethod(result.paymentMethod.id)
      }
    }
  }

  async setupNewCard () {
    const data = {
      payment_method: {
        card: this.card,
        billing_details: {
          name: this.nameTarget.value
        }
      }
    }

    const result = await this.stripe.confirmCardSetup(this.setupIntentValue, data)

    if (result.error) {
      this.showError(result.error.message)
    } else {
      this.handlePaymentMethod(result.setupIntent.payment_method)
    }
  }

  handlePaymentMethod (payment_method_id) {
    this.addHiddenField('processor', 'stripe')
    this.addHiddenField('payment_method_token', payment_method_id)
    this.completeAndsubmit()
  }

  addHiddenField (name, value) {
    const hiddenInput = document.createElement('input')
    hiddenInput.setAttribute('type', 'hidden')
    hiddenInput.setAttribute('name', name)
    hiddenInput.setAttribute('value', value)
    this.formTarget.appendChild(hiddenInput)
  }

  async handleCardPayment () {
    // Handle an existing payment that needs confirmation
    const result = await this.stripe.confirmCardPayment(this.paymentIntentValue)

    if (result.error) {
      this.showError(result.error.message)
    } else if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
      window.Turbo.clearCache()
      window.Turbo.visit('/')
    }
  }

  showError (message) {
    this.dispatch('error', { detail: { error: message } })
    this.errorTarget.textContent = message
    this.errorTarget.scrollIntoView({ behavior: 'smooth', block: "center"})
    setTimeout(() => enableForm(this.formTarget), 100)
  }

  completeAndsubmit () {
    const event = this.dispatch('complete')
    if (event.defaultPrevented) return
    this.formTarget.submit()
  }
}
